import { ReactNode, useEffect, useRef } from 'react';
import { useFocusWithin } from '@react-aria/interactions';
import styled from 'styled-components';

import UserGeneratedContentStyle from 'packages/kakapo-ui-kit/components/common/UserGeneratedContentStyle';
import RelativeContainer from 'packages/kakapo-ui-kit/components/atoms/RelativeContainer';
import Paragraph from 'packages/kakapo-ui-kit/components/common/Paragraph';
import HorizontalRule from 'packages/kakapo-ui-kit/components/common/HorizontalRule';
import Column from 'packages/kakapo-ui-kit/components/grid/Column';

const Element = styled.div<{
	grow?: boolean;
	wide?: boolean;
	width?: number;
	center?: boolean;
	tiny?: boolean;
	styleContent?: boolean;
	padding?: boolean;
}>`
	z-index: 9;
	${(props) =>
		props.center
			? `
	transform: translateX(-50%) translateY(calc(-100% - 29px));
	left: 50%;
	`
			: `
	transform: translateY(calc(-100% - 29px));
	right: 0;
	`}
	min-width: ${(props) => props.width ?? (props.wide ? 400 : props.tiny ? 100 : 300)}px;
	max-width: ${(props) => props.width ?? (props.wide ? 600 : props.tiny ? 100 : 600)}px;
	${(props) =>
		props.grow
			? undefined
			: `
		max-height: 300px;
		overflow: auto;
	`}

	background-color: ${(props) => props.theme.colors.white};
	box-shadow: 0 1px 8px 0 rgba(0, 0, 0, 0.3);
	border-radius: 6px;
	position: absolute;
	z-index: 10;
	padding: ${(props) => (props.padding ?? true ? 18 : 0)}px;
	margin-top: 10px;
	outline: none !important;

	${(props) => (props.styleContent !== false ? UserGeneratedContentStyle : null)}
`;

const ArrowElement = styled.div`
	position: absolute;
	z-index: 11;
	width: 100%;
	height: 14px;
	top: -14px;

	// Hidden object behind the arrow of the tooltip that adds shadow
	&:before {
		content: '';
		position: absolute;
		z-index: -1;
		top: -5px;
		left: 50%;
		transform: translateX(-50%);
		width: 2px;
		height: 2px;
		background-color: red;
		box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.3);
		display: block;
	}

	// Arrow of the tooltip
	&:after {
		content: '';
		position: absolute;
		top: -5px;
		left: 50%;
		transform: translateX(-50%);
		z-index: 8;
		border: 5px solid transparent;
		border-color: ${(props) => props.theme.colors.white} transparent transparent transparent;
		display: block;
	}
`;

interface IProps {
	tabIndex?: number;
	title?: string;
	wide?: boolean;
	center?: boolean;
	width?: number;
	tiny?: boolean;
	grow?: boolean;
	padding?: boolean;
	styleContent?: boolean;
	children?: ReactNode;
	onBlur?: () => void;
}

export default function Tooltip({
	title,
	wide,
	center,
	width,
	tiny,
	grow,
	styleContent,
	children,
	padding,
	onBlur,
}: IProps) {
	const { focusWithinProps } = useFocusWithin({
		onBlurWithin: () => onBlur?.(),
	});

	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		ref.current?.focus();
	}, []);

	return (
		<>
			<Element
				wide={wide}
				width={width}
				grow={grow}
				center={center}
				tiny={tiny}
				padding={padding}
				tabIndex={-1}
				onMouseDown={(event) => {
					event.currentTarget?.focus();
					event.stopPropagation();
				}}
				styleContent={styleContent}
				ref={ref}
				{...focusWithinProps}
			>
				{title && (
					<Column mb={2}>
						<Paragraph color="slateBlue">{title}</Paragraph>
						<HorizontalRule color="lightBlue" />
					</Column>
				)}

				{children}
			</Element>

			<RelativeContainer>
				<ArrowElement />
			</RelativeContainer>
		</>
	);
}
